import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { bookingActions } from "../../store/booking-slice";
import { parse } from "date-fns";
import CheckReturningGuest from "../ReturningGuestApi/CheckReturningGuest";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const URLPrefill = ({ clearQuery = true }) => {
  const dispatch = useDispatch();
  const hasParsedURL = useRef(false);

  // --- local state to drive the headless CheckReturningGuest components ---
  const [g1Email, setG1Email] = useState("");
  const [g2Email, setG2Email] = useState("");
  const [g1APICheck, setG1APICheck] = useState(false);
  const [g2APICheck, setG2APICheck] = useState(false);
  const [g1IsReturning, setG1IsReturning] = useState(undefined); // true/false/undefined
  const [g2IsReturning, setG2IsReturning] = useState(undefined); // true/false/undefined

  useEffect(() => {
    if (hasParsedURL.current) return;
    hasParsedURL.current = true;

    const search = new URLSearchParams(window.location.search);

    // --- guests/rooms ---
    let totalGuests = parseInt(search.get("guests") || "1", 10);
    let totalRooms = parseInt(search.get("rooms") || "1", 10);
    if (![1, 2].includes(totalGuests)) totalGuests = 1;
    if (![1, 2].includes(totalRooms)) totalRooms = 1;
    if (totalRooms > totalGuests) totalRooms = totalGuests;

    dispatch(
      bookingActions.setTotalGuestsAccommodation({
        totalGuests,
        totalRooms,
      })
    );

    // --- dates ---
    const start = search.get("start") || undefined; // dd-MM-yyyy
    const end = search.get("end") || undefined; // dd-MM-yyyy
    const formattedStart = start?.replace(/-/g, "/"); // dd/MM/yyyy
    const formattedEnd = end?.replace(/-/g, "/"); // dd/MM/yyyy

    const isValidFormat =
      /^\d{2}\/\d{2}\/\d{4}$/.test(formattedStart || "") &&
      /^\d{2}\/\d{2}\/\d{4}$/.test(formattedEnd || "");

    if (start && end && isValidFormat) {
      const checkIn = parse(formattedStart, "dd/MM/yyyy", new Date());
      const checkOut = parse(formattedEnd, "dd/MM/yyyy", new Date());
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const nights = Math.floor((checkOut - checkIn) / (1000 * 60 * 60 * 24));
      const isFutureCheckIn = checkIn >= today;
      const isValidStay = nights >= 5 && nights <= 22;

      if (isFutureCheckIn && isValidStay) {
        dispatch(
          bookingActions.setBookingDates({
            checkIn: formattedStart,
            checkOut: formattedEnd,
          })
        );
        dispatch(
          bookingActions.setDateCalendarTouched({ dateCalendarTouched: true })
        );
        dispatch(bookingActions.toggleDatesConfirmed());
      } else {
        console.warn("❌ Ignoring invalid URL dates", {
          checkIn: formattedStart,
          checkOut: formattedEnd,
          nights,
          isFutureCheckIn,
          isValidStay,
        });
      }
    }

    // --- returning emails via URL ---
    // alias: ?email= works as g1
    const g1raw = (search.get("g1") || search.get("email") || "")
      .trim()
      .toLowerCase();
    const g2raw = (search.get("g2") || "").trim().toLowerCase();

    if (EMAIL_RE.test(g1raw)) {
      setG1Email(g1raw);
      setG1APICheck(true); // triggers CheckReturningGuest for guest 1
    }

    if (totalGuests === 2 && EMAIL_RE.test(g2raw)) {
      setG2Email(g2raw);
      setG2APICheck(true); // triggers CheckReturningGuest for guest 2
    }

    if (clearQuery) {
      // Clean the URL (remove query string but keep path)
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [dispatch, clearQuery]);

  // When the headless checkers finish and confirm returning status, update Redux.
  useEffect(() => {
    if (g1IsReturning === true && g1Email) {
      dispatch(
        bookingActions.setGuest1Returning({
          guest1Returning: true,
          guest1ReturningEmail: g1Email,
        })
      );
      // reset local flag to avoid repeated dispatches on re-renders
      setG1IsReturning(undefined);
    }
  }, [g1IsReturning, g1Email, dispatch]);

  useEffect(() => {
    if (g2IsReturning === true && g2Email) {
      dispatch(
        bookingActions.setGuest2Returning({
          guest2Returning: true,
          guest2ReturningEmail: g2Email,
        })
      );
      setG2IsReturning(undefined);
    }
  }, [g2IsReturning, g2Email, dispatch]);

  return (
    <>
      {/* Headless API triggers: render nothing, just perform checks when *APICheck is true */}
      <CheckReturningGuest
        email={g1Email}
        guestNumber={1}
        emailAPICheck={g1APICheck}
        setEmailAPICheck={setG1APICheck}
        setGuestIsReturning={setG1IsReturning}
      />
      <CheckReturningGuest
        email={g2Email}
        guestNumber={2}
        emailAPICheck={g2APICheck}
        setEmailAPICheck={setG2APICheck}
        setGuestIsReturning={setG2IsReturning}
      />
    </>
  );
};

export default URLPrefill;
